/**
 * Hero — the first viewport. Aurora background, big arcade title, primary
 * CTAs, signature pills, and a floating "puzzle preview" mockup on the right
 * to telegraph what the product feels like.
 */
import { motion } from 'motion/react';
import { Aurora } from '@/components/Aurora';
import { GlassButton } from '@/components/GlassButton';
import { Icon, type IconName } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';

export function HomeHero(): JSX.Element {
  return (
    <section className="relative isolate overflow-hidden scanlines">
      <div className="absolute inset-0 -z-10 bg-grid opacity-60" />
      <div className="absolute inset-0 -z-10 bg-radial-warm" />
      <div className="absolute inset-0 -z-10 bg-radial-mint" />
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[80vh] opacity-50">
        <Aurora colorStops={['#00f0ff', '#aaff00', '#ff2e88']} amplitude={1.3} blend={0.55} />
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-24 md:pb-36">
        <Reveal>
          <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-arcade tracking-[0.15em] sm:tracking-[0.2em] text-honey-300/90 mb-6 whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-honey-400 animate-pulse-soft" />
            ARCADE FOR ENGLISH · v2.0
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-extrabold leading-[0.95] tracking-tight text-white max-w-4xl">
            <span className="block">Combo · Perfect ·</span>
            <span className="block text-gradient-warm">Speak it out</span>
          </h1>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-4 font-display text-2xl md:text-4xl lg:text-5xl text-cream-100 font-bold">
            把英语学习真正做成 <span className="text-honey-400 text-neon">街机游戏</span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-cream-200/90 leading-relaxed text-balance">
            连击、Perfect 评分、键盘音效、AI 选词问答、自定义课程 —
            5 个别处没有的设计。
            <span className="text-cream-400">每天 15 分钟，玩着玩着英语就上来了。</span>
          </p>
        </Reveal>

        <Reveal delay={0.16}>
          <div className="mt-9 flex flex-wrap gap-3">
            <GlassButton to="/app" size="lg" variant="solid">
              INSERT COIN · 立即开玩 →
            </GlassButton>
            <GlassButton to="/app/create" size="lg">
              <Icon name="create" size="md" /> 5 秒造一节
            </GlassButton>
          </div>
        </Reveal>

        <Reveal delay={0.22}>
          <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-cream-400 font-arcade tracking-widest">
            <Pill icon="listen"  text="KEYBOARD SFX" />
            <Pill icon="perfect" text="COMBO ×5 GODLIKE" />
            <Pill icon="target"  text="UGC EDITOR" />
          </div>
        </Reveal>
      </div>

      {/* Floating puzzle preview — only visible on lg+ */}
      <motion.div
        initial={{ opacity: 0, y: 24, rotate: -2 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ delay: 0.4, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:block absolute right-12 top-32 w-[360px]"
      >
        <HeroPuzzlePreview />
      </motion.div>
    </section>
  );
}

function Pill({ icon, text }: { icon: IconName; text: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-honey-400/20 bg-ink-900/40 px-3 py-1.5">
      <Icon name={icon} size="sm" />
      <span>{text}</span>
    </span>
  );
}

function HeroPuzzlePreview(): JSX.Element {
  const tiles = ['Could', 'you', 'kindly', 'share', 'the', 'report', '?'];
  return (
    <div className="glass-card rounded-3xl p-5 relative overflow-hidden">
      {/* Floating combo */}
      <div className="absolute -top-3 -right-3 z-10">
        <div className="font-score text-4xl text-honey-400 text-neon">×7</div>
        <div className="text-[8px] uppercase tracking-[0.2em] font-arcade text-honey-300 text-right">COMBO</div>
      </div>

      <div className="text-xs text-cream-400 mb-3 flex items-center justify-between font-arcade tracking-widest">
        <span className="inline-flex items-center gap-1.5">
          <Icon name="puzzle" size="sm" /> PUZZLE · LV.3
        </span>
        <span className="text-mint-400">+30 XP</span>
      </div>
      <div className="text-sm text-cream-300 mb-4 leading-relaxed">
        能否方便分享一下这份报告？
      </div>
      <div className="flex flex-wrap gap-2">
        {tiles.map((t, i) => (
          <motion.span
            key={t + i}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 + i * 0.06 }}
            className={`px-3 py-1.5 rounded-xl text-sm font-medium border ${
              i < 3
                ? 'bg-honey-400/15 border-honey-400/40 text-honey-200'
                : 'bg-ink-800/60 border-white/[0.1] text-cream-200'
            }`}
          >
            {t}
          </motion.span>
        ))}
      </div>
      <div className="mt-4 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: '60%' }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="h-full bg-gradient-to-r from-honey-400 via-mint-400 to-ember-400"
        />
      </div>
    </div>
  );
}
